import { Avatar, Box, Checkbox, List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Typography, } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Autosizer, Scrollbar } from 'app/components';
import { TransportGroupRoundedIcon } from 'app/icons';
import { useAsyncAction, usePermission } from 'app/services';
import { userTransportGroupDelete, userTransportGroupListDelete, userTransportGroupListPush, userTransportGroupPush } from 'app/store/user';
import React, { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const UserTransportGroupList = ({ searchValue }) => {
    const editPermission = usePermission(['create', 'update', 'delete']);
    const { selectedItem } = useSelector((state) => state.user);
    const transportGroups = selectedItem.transportGroups;
    const checkedLength = transportGroups === null || transportGroups === void 0 ? void 0 : transportGroups.filter((item) => item.checked).length;
    const isCheckedAll = checkedLength === (transportGroups === null || transportGroups === void 0 ? void 0 : transportGroups.length);
    const { asyncAction: deleteSubmit, isLoading: isDeleteSubmit } = useAsyncAction(userTransportGroupDelete, [selectedItem]);
    const { asyncAction: deleteGroup, isLoading: isDeleteGroup } = useAsyncAction(userTransportGroupListDelete, [selectedItem]);
    const { asyncAction: addGroup, isLoading: isAddGroup } = useAsyncAction(userTransportGroupListPush, [selectedItem]);
    const { asyncAction: pushSubmit, isLoading: isPushSubmit } = useAsyncAction(userTransportGroupPush, [selectedItem]);
    const items = useMemo(() => {
        if (searchValue === null || searchValue === void 0 ? void 0 : searchValue.length) {
            return transportGroups.filter((item) => item.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
        }
        return transportGroups;
    }, [selectedItem, searchValue]);
    const handleItemClick = (value) => {
        if (!editPermission)
            return;
        value.checked ? deleteSubmit(selectedItem.id, value.id) : pushSubmit(selectedItem.id, value.id);
    };
    const handleCheckAll = () => {
        if (!editPermission || isAddGroup || isDeleteGroup)
            return;
        if (isCheckedAll) {
            deleteGroup(selectedItem.id, transportGroups.map((item) => item.id));
        }
        else {
            addGroup(selectedItem.id, transportGroups.filter((item) => !item.checked).map((item) => item.id));
        }
    };
    return (React.createElement(Box, { component: 'div', sx: (theme) => ({
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            '.simplebar-offset > div': {
                paddingBottom: '36px',
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.MuiListItem-root': {
                width: '100%',
                userSelect: 'none',
                padding: theme.spacing(0),
                borderRadius: '0',
                '.MuiListItemIcon-root': {
                    justifyContent: 'end',
                    '.MuiCheckbox-root': {
                        padding: theme.spacing(0),
                        marginRight: theme.spacing(0),
                        svg: {
                            fontSize: '18px',
                        },
                    },
                },
                '.MuiListItemAvatar-root': {
                    minWidth: '36px',
                },
                '.MuiAvatar-circular': {
                    width: '24px',
                    height: '24px',
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    svg: {
                        fontSize: '16px',
                        path: {
                            fill: theme.palette.primary.main,
                        },
                    },
                },
                '.MuiListItemButton-root': {
                    padding: theme.spacing(1, 2),
                    borderRadius: '0',
                },
                '.MuiListItemText-root': {
                    margin: theme.spacing(0),
                },
            },
        }) },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(Box, { sx: {
                    textAlign: 'end',
                    paddingRight: '14px',
                    cursor: editPermission ? 'pointer' : 'default',
                    font: 'inherit',
                    display: editPermission ? 'block' : 'none',
                }, onClick: handleCheckAll },
                React.createElement(Typography, { component: 'span' }, "\u0412\u044B\u0434\u0435\u043B\u0438\u0442\u044C \u0432\u0441\u0451"),
                React.createElement(Checkbox, { size: 'small', edge: 'end', disableRipple: true, checked: isCheckedAll, disabled: isAddGroup || isDeleteGroup })),
            React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(FixedSizeList, { height: height, itemSize: 36, overscanCount: 10, itemCount: items.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, innerElementType: UserTransportGroupListInnerElementType, itemData: {
                    items,
                    onClick: handleItemClick,
                    isPushSubmit,
                    isDeleteSubmit,
                    isAddGroup,
                    isDeleteGroup,
                    editPermission,
                } }, UserTransportGroupListItemRender))))))));
};
const UserTransportGroupListInnerElementType = forwardRef((props, ref) => (React.createElement(List, Object.assign({ ref: ref, disablePadding: true }, props))));
const UserTransportGroupListItemRender = ({ data, index, style }) => {
    var _a, _b, _c;
    const { items, onClick, isPushSubmit, isDeleteSubmit, isAddGroup, isDeleteGroup, editPermission } = data;
    return (React.createElement(ListItem, { key: (_a = items[index]) === null || _a === void 0 ? void 0 : _a.id, style: style, onClick: () => editPermission && onClick(items[index]) },
        React.createElement(ListItemButton, { disabled: !editPermission },
            React.createElement(ListItemAvatar, null,
                React.createElement(Avatar, null,
                    React.createElement(TransportGroupRoundedIcon, { fontSize: 'small' }))),
            React.createElement(ListItemText, { primary: (_b = items[index]) === null || _b === void 0 ? void 0 : _b.name }),
            React.createElement(ListItemIcon, null,
                React.createElement(Checkbox, { size: 'small', edge: 'end', disableRipple: true, disabled: !editPermission || isPushSubmit || isDeleteSubmit || isAddGroup || isDeleteGroup, checked: (_c = items[index]) === null || _c === void 0 ? void 0 : _c.checked })))));
};
export default UserTransportGroupList;
